import { SectionHeader } from "@/components/ui/SectionHeader";
import { Container } from "@/components/ui/Container";
import { FeatureCard } from "@/components/ui/FeatureCard";
import { FeatureBlock } from "@/components/ui/FeatureBlock";
import { SmallFeatureCard } from "@/components/ui/SmallFeatureCard";
import {
  ReportsIllustration,
  ChatIllustration,
  SpeakersIllustration,
  AssistantIllustration,
  BotIllustration,
  LinkTranscribeIllustration,
} from "./FeatureIllustrations";
import { landingContent } from "@/content/landing";
import { siteConfig } from "@/config/site";
import { FiSmartphone } from "react-icons/fi";

const Icon32 = (name: string) => (
  <span
    aria-hidden="true"
    className="block size-[32px] bg-[var(--brand-primary)]"
    style={{
      WebkitMaskImage: `url(/assets/hero-icons/${name}.svg)`,
      maskImage: `url(/assets/hero-icons/${name}.svg)`,
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskPosition: "center",
      maskPosition: "center",
      WebkitMaskSize: "contain",
      maskSize: "contain",
    }}
  />
);

const MessengerIcon = (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true" className="size-[40px]">
    <circle cx="20" cy="20" r="20" fill="#229ED9" />
    <path
      d="M8.92 19.66c5.86-2.55 9.77-4.24 11.72-5.05 5.58-2.32 6.74-2.73 7.5-2.74.16 0 .53.04.77.23.2.16.26.38.28.53.03.16.06.5.04.78-.3 3.18-1.62 10.9-2.3 14.46-.28 1.5-.84 2.01-1.39 2.06-1.18.11-2.08-.78-3.23-1.53-1.8-1.18-2.81-1.92-4.56-3.07-2.02-1.33-.71-2.06.44-3.25.3-.31 5.5-5.05 5.6-5.48.02-.05.02-.25-.09-.36-.12-.1-.29-.07-.41-.04-.18.04-2.98 1.9-8.4 5.56-.79.55-1.51.81-2.16.8-.71-.02-2.08-.4-3.1-.73-1.24-.41-2.24-.62-2.15-1.31.05-.36.54-.73 1.47-1.12Z"
      fill="#fff"
    />
  </svg>
);

const MobileIcon = <FiSmartphone aria-hidden="true" className="size-[30px] text-primary" strokeWidth={1.8} />;

interface FeaturesV2Props {
  className?: string;
}

export function FeaturesV2({ className = "" }: FeaturesV2Props) {
  const featureContent = landingContent.features;
  const isScriptario = siteConfig.siteId === "scriptario";

  return (
    <section id="features" className={`relative flex flex-col items-center gap-[40px] py-[80px] overflow-hidden ${className}`}>
      {/* Soft airy brand glow centred horizontally and vertically: a single radial ellipse fading to
          fully transparent toward the left/right edges (white at the section margins) and toward the
          top/bottom edges, so it reads as a soft centred wash rather than a full-width plate. Almost no
          tint sits directly behind the heading. No hard seam, no flat fill. Same on every breakpoint. */}
      <div
        className="absolute inset-0 pointer-events-none max-tablet:hidden"
        style={{
          background:
            "radial-gradient(ellipse 62% 22% at 50% 20%, var(--brand-primary-soft) 0%, rgba(255,255,255,0) 72%), radial-gradient(ellipse 62% 22% at 50% 50%, var(--brand-primary-soft) 0%, rgba(255,255,255,0) 72%), radial-gradient(ellipse 62% 24% at 50% 80%, var(--brand-primary-soft) 0%, rgba(255,255,255,0) 72%)",
        }}
      />
      {/* mobile (<769): stacked layout makes the section very tall — tile a centred blob every
          fixed px so coverage stays even at any height; edge-masked so no seam / no header tint. */}
      <div
        className="absolute inset-0 pointer-events-none hidden max-tablet:block"
        style={{
          background:
            "color-mix(in srgb, var(--brand-primary-soft) 50%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0px, #000 170px, #000 calc(100% - 120px), transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0px, #000 170px, #000 calc(100% - 120px), transparent 100%)",
        }}
      />

      <Container className="flex flex-col items-center gap-[40px]">
      <SectionHeader className="relative z-[1]"
        title={featureContent.title}
        subtitle={featureContent.subtitle}
      />

      {/* Card rows — inter-row rhythm 16px (consistent with grid gaps) */}
      <div className="relative flex flex-col items-center gap-[16px] w-full">
      {/* Row 1: Feature cards */}
      <div className="relative flex gap-[16px] w-full max-tablet:flex-col">
        {featureContent.cards.map((f) => (
          <FeatureCard key={f.title} className="max-desktop:flex-1" icon={Icon32(f.iconName)} title={f.title} description={f.description} />
        ))}
      </div>

      {/* Row 2: Feature blocks (equal width) */}
      <div className="relative flex gap-[16px] w-full max-desktop:flex-col">
        <FeatureBlock
          className="flex-1 min-w-0 h-[476px] max-desktop:w-full max-desktop:flex-none"
          title={featureContent.aiReports.title}
          description={featureContent.aiReports.description}
          titlePosition="top"
          mobileHeightClass="max-desktop:!h-[476px]"
        >
          <ReportsIllustration />
        </FeatureBlock>
        <FeatureBlock
          className="flex-1 min-w-0 h-[476px] max-desktop:w-full max-desktop:flex-none"
          title={featureContent.aiChat.title}
          description={featureContent.aiChat.description}
          titlePosition="bottom"
          narrowText
          mobileHeightClass="max-desktop:!h-[476px]"
          mobileChatLayout
        >
          <ChatIllustration />
        </FeatureBlock>
      </div>

      {/* Row 3: Wide feature */}
      <div className="relative w-full">
        <FeatureBlock
          className="w-full h-[380px]"
          title={featureContent.speakers.title}
          description={featureContent.speakers.description}
          mobileHeightClass="max-desktop:!h-[380px]"
        >
          <SpeakersIllustration />
        </FeatureBlock>
      </div>

      {/* Row 4: Another wide feature */}
      <div className="relative w-full">
        <FeatureBlock
          className="w-full h-[400px]"
          title={featureContent.meetingAssistant.title}
          description={featureContent.meetingAssistant.description}
          narrowText
          mobileHeightClass="max-desktop:!h-[400px]"
        >
          <AssistantIllustration />
        </FeatureBlock>
      </div>

      {/* Row 5: Two side-by-side features (≈729:535 ratio) */}
      <div className="relative flex gap-[16px] w-full max-desktop:flex-col">
        <FeatureBlock
          className={`flex-[729] min-w-0 h-[400px] max-desktop:w-full max-desktop:flex-none ${isScriptario ? "max-desktop:h-[520px]" : "max-desktop:h-[400px]"}`}
          title={featureContent.telegramBot.title}
          description={featureContent.telegramBot.description}
          layout="side"
          icon={isScriptario ? MobileIcon : MessengerIcon}
          mobileFigmaText
        >
          <BotIllustration />
        </FeatureBlock>
        <FeatureBlock
          className="flex-[535] min-w-0 h-[400px] max-desktop:w-full max-desktop:flex-none max-desktop:h-[400px]"
          title={featureContent.linkTranscription.title}
          description={featureContent.linkTranscription.description}
          mobileFigmaText
          mobileFixedHeight
        >
          <LinkTranscribeIllustration />
        </FeatureBlock>
      </div>

      {/* Row 6: Small feature cards */}
      <div className="relative flex gap-[16px] w-full max-tablet:flex-col">
        {featureContent.smallCards.map((f) => (
          <SmallFeatureCard
            key={f.title}
            className="max-desktop:flex-1"
            icon={Icon32(f.iconName)}
            title={f.title}
            description={f.description}
          />
        ))}
      </div>
      </div>
      </Container>
    </section>
  );
}
